import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { onUnauthorized } from '../api/client';
import { Admin } from '../types';
import { authStorage } from '../utils/storage';

interface AuthContextType {
  admin: Admin | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is already logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const hasTokens = authStorage.hasTokens();

      if (hasTokens) {
        try {
          // Try to get admin profile
          const profile = await authApi.getProfile();
          setAdmin(profile);
        } catch (error) {
          // Token invalid or expired, clear storage
          authStorage.clearTokens();
          setAdmin(null);
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Listen for unauthorized events from axios interceptor
  useEffect(() => {
    const unsubscribe = onUnauthorized(() => {
      // Clear admin state and redirect to login
      setAdmin(null);
      navigate('/admin/login');
    });

    return unsubscribe;
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });
      authStorage.setTokens(response.accessToken, response.refreshToken);
      setAdmin(response.admin);
      navigate('/admin/dashboard');
    } catch (error) {
      authStorage.clearTokens();
      throw error;
    }
  };

  const logout = () => {
    authApi.logout().catch(() => {
      // Ignore logout errors
    });
    authStorage.clearTokens();
    setAdmin(null);
    navigate('/admin/login');
  };

  const value: AuthContextType = {
    admin,
    login,
    logout,
    isAuthenticated: !!admin,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
